import { useTranslation } from 'react-i18next';

import { ApiError } from '~/api/errors';
import { CachedImage } from '~/components/common/cached-image/cached-image';
import { DeliveryStatusChip } from '~/features/deliveries/components/chips/delivery-status-chip';
import { DryRunBadge } from '~/features/deliveries/components/chips/dry-run-badge';
import { productOutcomeTone, syncStatusTone } from '~/features/deliveries/constants/status-tones';
import type { ProductLookupRow } from '~/features/deliveries/types/delivery-runs.types';
import { humanizeEnum } from '~/features/deliveries/utils/format-enum';
import { formatUtcNaiveDateTime } from '~/utils/date';
import { extractApiError } from '~/utils/extract-api-error';

import type { Product } from '../../types/catalog-operations.types';

interface Props {
  product: Product;
  row: ProductLookupRow | undefined;
  isLoading: boolean;
  error: unknown;
  /** Delivered / total attribute count for the small tally next to the outcome chip. */
  deliveredCount: number;
  totalCount: number;
}

/**
 * Header strip for the selected product: identity, the row-level delivery
 * verdict (outcome + destination sync status kept as two separate chips, per
 * the contract's own "don't collapse them" guidance), and the delivered vs
 * total attribute tally. Renders the same information regardless of whether
 * the delivery pane resolved — the Pre-enrichment/Enriched panes never wait on
 * this fetch.
 */
export function DeliveryCompareProductStrip({
  product,
  row,
  isLoading,
  error,
  deliveredCount,
  totalCount,
}: Props) {
  const { t } = useTranslation('catalog-operations');

  return (
    <div
      className="bg-background rounded-xl border p-4"
      data-testid="delivery-compare-product-strip"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 items-start gap-3">
          <CachedImage
            src={product.imageUrl}
            alt={product.title}
            className="h-14 w-14 shrink-0 rounded-lg border"
          />
          <div className="min-w-0">
            <p
              className="text-foreground truncate text-base font-semibold"
              data-testid="delivery-compare-product-strip-title"
            >
              {product.title}
            </p>
            <p
              className="text-muted-foreground truncate text-xs"
              data-testid="delivery-compare-product-strip-source-id"
            >
              {t('deliveryCompare.strip.sourceId', { id: product.sourceProductId })}
            </p>
            <p
              className="text-muted-foreground text-xs"
              data-testid="delivery-compare-product-strip-tally-value"
            >
              {t('deliveryCompare.strip.attributeTally', {
                delivered: deliveredCount,
                total: totalCount,
              })}
            </p>
          </div>
        </div>

        <DeliveryPaneSummary row={row} isLoading={isLoading} error={error} />
      </div>
    </div>
  );
}

function DeliveryPaneSummary({
  row,
  isLoading,
  error,
}: {
  row: ProductLookupRow | undefined;
  isLoading: boolean;
  error: unknown;
}) {
  const { t } = useTranslation('catalog-operations');

  if (isLoading) {
    return (
      <p className="text-muted-foreground text-xs" data-testid="delivery-compare-delivery-loading">
        {t('deliveryCompare.strip.loading')}
      </p>
    );
  }

  if (error) {
    const status = error instanceof ApiError ? error.statusCode : null;
    const key =
      status === 503
        ? 'deliveryCompare.strip.errors.unavailable'
        : status === 404
          ? 'deliveryCompare.strip.errors.connectionMismatch'
          : status === 403
            ? 'deliveryCompare.strip.errors.forbidden'
            : 'deliveryCompare.strip.errors.generic';
    return (
      <p
        className="text-error max-w-64 text-right text-xs"
        data-testid="delivery-compare-delivery-error"
      >
        {extractApiError(error, t(key))}
      </p>
    );
  }

  if (!row) {
    return (
      <div className="max-w-64 text-right" data-testid="delivery-compare-delivery-empty">
        <p className="text-muted-foreground text-xs font-medium">
          {t('deliveryCompare.strip.noRecord')}
        </p>
        <p className="text-muted-foreground text-[11px]">
          {t('deliveryCompare.strip.retentionHint')}
        </p>
      </div>
    );
  }

  const outcome = row.outcome ?? 'UNKNOWN';
  const syncStatus = row.syncStatus;

  return (
    <div
      className="flex flex-col items-end gap-1.5"
      data-testid="delivery-compare-delivery-summary"
    >
      <div
        className="flex flex-wrap items-center justify-end gap-1.5"
        data-testid="delivery-compare-delivery-status"
      >
        {row.runMode === 'MOCK' ? <DryRunBadge testId="delivery-compare-dry-run-badge" /> : null}
        <DeliveryStatusChip
          tone={productOutcomeTone(outcome)}
          label={humanizeEnum(outcome)}
          testId="delivery-compare-outcome-chip"
        />
        {syncStatus ? (
          <DeliveryStatusChip
            testId="delivery-compare-sync-status-chip"
            tone={syncStatusTone(syncStatus)}
            label={
              row.syncStatusAt
                ? humanizeEnum(syncStatus)
                : t('deliveryCompare.strip.awaitingReview')
            }
          />
        ) : null}
      </div>
      {row.deliveryTimestamp ? (
        <p
          className="text-muted-foreground text-[11px] tabular-nums"
          data-testid="delivery-compare-delivery-timestamp"
        >
          {formatUtcNaiveDateTime(row.deliveryTimestamp)}
          {typeof row.attemptCount === 'number' && row.attemptCount > 1
            ? ` · ${t('deliveryCompare.strip.attempts', { count: row.attemptCount })}`
            : ''}
        </p>
      ) : null}
      {outcome === 'FAILED' || outcome === 'VALIDATION_FAILED' ? (
        <p
          className="text-error max-w-64 text-right text-[11px]"
          data-testid="delivery-compare-delivery-error-message"
        >
          {/* `??` alone wouldn't cover it: an empty-string errorMessage means
              "no detail recorded" on this wire, same as an absent one — this
              ternary treats both as falsy on purpose. */}
          {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing */}
          {row.errorMessage ? row.errorMessage : t('deliveryCompare.strip.noErrorDetail')}
        </p>
      ) : null}
    </div>
  );
}
